import { FileItem, FileStatus } from '@sib/types';
import { create } from 'zustand';

export interface FileUploadStore {
  onFilesUploaded: (() => void) | null;
  setOnFilesUploaded: (onFilesUploaded: (() => void) | null) => void;
  files: FileItem[];
  uploadControllers: Record<string, AbortController>;
  getFiles: () => FileItem[];
  setFiles: (files: FileItem[]) => void;
  addFiles: (newFiles: FileItem[]) => void;
  updateFile: (updatedFile: FileItem) => void;
  removeFile: (fileId: string) => void;
  selectAll: (selected: boolean) => void;
  deleteSelectedFiles: () => void;
  removeUploadedFiles: () => void;
  markSelectedFilesAsSensitive: () => void;
  resetFiles: () => void;
  getAreFilesUploading: () => boolean;
  registerUploadController: (
    fileId: string,
    controller: AbortController
  ) => void;
  unregisterUploadController: (fileId: string) => void;
  cancelAllUploads: () => void;
}

export const createFileUploadStore = () =>
  create<FileUploadStore>((set, get) => ({
    onFilesUploaded: null,
    setOnFilesUploaded: onFilesUploaded => set({ onFilesUploaded }),
    files: [],
    uploadControllers: {},
    getFiles: () => get().files,
    setFiles: files => set({ files }),
    addFiles: newFiles =>
      set(state => ({ files: [...state.files, ...newFiles] })),
    updateFile: updatedFile =>
      set(state => ({
        files: state.files.map(file =>
          file.id === updatedFile.id ? updatedFile : file
        ),
      })),
    removeFile: fileId =>
      set(state => ({
        files: state.files.filter(file => file.id !== fileId),
      })),
    selectAll: selected =>
      set(state => ({
        files: state.files.map(file => ({ ...file, selected })),
      })),
    deleteSelectedFiles: () =>
      set(state => ({
        files: state.files.filter(file => !file.selected),
      })),
    removeUploadedFiles: () =>
      set(state => ({
        files: state.files.filter(file => file.status !== FileStatus.Uploaded),
      })),
    markSelectedFilesAsSensitive: () =>
      set(state => ({
        files: state.files.map(file =>
          file.selected ? { ...file, isSensitive: true } : file
        ),
      })),
    resetFiles: () => set({ files: [], uploadControllers: {} }),
    getAreFilesUploading: () =>
      get().files.some(file => file.status === FileStatus.Uploading),
    registerUploadController: (fileId, controller) =>
      set(state => ({
        uploadControllers: { ...state.uploadControllers, [fileId]: controller },
      })),
    unregisterUploadController: fileId =>
      set(state => {
        const { [fileId]: _, ...nextControllers } = state.uploadControllers;

        return { uploadControllers: nextControllers };
      }),
    cancelAllUploads: () =>
      set(state => {
        Object.values(state.uploadControllers).forEach(controller =>
          controller.abort()
        );

        return { uploadControllers: {} };
      }),
  }));
